import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { savedReceiversAPI } from "../../api/savedReceivers/savedReceiver";

const initialState = {
    savedReceivers: [],
    unauthorized: false,
    statuses: {
        uploading: null,
        updating: null,
        deleting: null,
    },
    errors: {
        uploading: null,
        updating: null,
        deleting: null,
    },
    updatingPopup: {
        opened: false,
        savedReceiverID: null,
    },
    deletePopup: {
        opened: false,
        savedReceiverID: null,
    },
};

export const uploadSavedReceivers = createAsyncThunk(
    "savedReceivers/uploadSavedReceivers",
    async (_, { rejectWithValue }) => {
        const resp = await savedReceiversAPI.getSavedReceivers();
        if (resp.ok) {
            return resp.data;
        }
        if (resp.unauthorized) {
            return rejectWithValue({ err: "unauthorized" });
        }
        if (resp.err) {
            return rejectWithValue({ err: resp.err });
        }
        return rejectWithValue({
            err: "unable_to_load_saved_receivers",
        });
    }
);

export const updateSavedReceiver = createAsyncThunk(
    "savedReceivers/updateSavedReceiver",
    async (data, { rejectWithValue }) => {
        const resp = await savedReceiversAPI.updateSavedReceiver(data);
        if (resp.ok) {
            return resp.data;
        }
        if (resp.unauthorized) {
            return rejectWithValue({ err: "unauthorized" });
        }
        if (resp.err) {
            return rejectWithValue({ err: resp.err });
        }
        return rejectWithValue({ err: "unable_to_update_saved_receiver" });
    }
);

export const deleteSavedReceiver = createAsyncThunk(
    "savedReceivers/deleteSavedReceiver",
    async (id, { rejectWithValue }) => {
        if(!id) {
            return rejectWithValue({ err: "unable_to_update_saved_receiver" });
        }
        const resp = await savedReceiversAPI.deleteSavedReceiver(id);
        if (resp.ok) {
            return {id};
        }
        if (resp.unauthorized) {
            return rejectWithValue({ err: "unauthorized" });
        }
        if (resp.err) {
            return rejectWithValue({ err: resp.err });
        }
        return rejectWithValue({ err: "unable_to_update_saved_receiver" });
    }
);

const savedReceiversSlice = createSlice({
    name: "savedReceivers",
    initialState,
    reducers: {
        openUpdatingSavedReceiverPopup(state, action) {
            if (!action.payload) return;
            state.updatingPopup.opened = true;
            state.updatingPopup.savedReceiverID = action.payload;
        },
        closeUpdatingSavedReceiverPopup(state) {
            state.updatingPopup.opened = false;
            state.updatingPopup.savedReceiverID = null;
        },
        openDeleteSavedReceiverPopup(state, action) {
            if (!action.payload) return;
            state.deletePopup.opened = true;
            state.deletePopup.savedReceiverID = action.payload;
        },
        closeDeleteSavedReceiverPopup(state) {
            state.deletePopup.opened = false;
            state.deletePopup.savedReceiverID = null;
        },
        setSavedReceiversUpdatingStatus(state, action) {
            state.statuses.updating = action.payload;
        },
        setSavedReceiversUpdatingError(state, action) {
            state.errors.updating = action.payload;
        },
        setUnauthorized(state, action) {
            if(!action.payload) {
                state.unauthorized = false
                return;
            }
            state.unauthorized = action.payload
        }
    },
    extraReducers(builder) {
        builder
            .addCase(uploadSavedReceivers.pending, (state, action) => {
                state.statuses.uploading = "pending";
                state.errors.uploading = null;
            })
            .addCase(uploadSavedReceivers.fulfilled, (state, action) => {
                state.statuses.uploading = "fulfilled";
                state.savedReceivers = action.payload;
            })
            .addCase(uploadSavedReceivers.rejected, (state, action) => {
                state.statuses.uploading = "rejected";
                if (action.payload?.err === "unauthorized") {
                    state.unauthorized = true;
                    return
                }
                if (action.payload?.err) {
                    state.errors.uploading = action.payload.err;
                }
            })


        builder
            .addCase(updateSavedReceiver.pending, (state, action) => {
                state.statuses.updating = "pending";
                state.errors.updating = null;
            })
            .addCase(updateSavedReceiver.fulfilled, (state, action) => {
                if (!action.payload || !action.payload.id) {
                    state.statuses.updating = "rejected";
                    state.errors.updating = "unable_to_update_saved_receiver";
                    return;
                }
                state.statuses.updating = "fulfilled";
                state.errors.updating = null;
                state.savedReceivers = state.savedReceivers.map((receiver) => {
                    if (receiver.id === action.payload.id) {
                        return action.payload;
                    }
                    return receiver;
                });
            })
            .addCase(updateSavedReceiver.rejected, (state, action) => {
                state.statuses.updating = "rejected";
                if (action.payload?.err === "unauthorized") {
                    state.unauthorized = true;
                }
                if (action.payload?.err) {
                    state.errors.updating = action.payload.err;
                    return
                }
                state.errors.updating = "unable_to_update_saved_receiver";
            });
            
            
            builder
            .addCase(deleteSavedReceiver.pending, (state, action) => {
                state.statuses.deleting = "pending";
                state.errors.deleting = null;
            })
            .addCase(deleteSavedReceiver.fulfilled, (state, action) => {
                const id = action.payload?.id;
                if(!id) {
                    state.statuses.deleting = "rejected";
                    state.errors.deleting = "unable_to_update_saved_receiver";
                    return;
                }
                state.savedReceivers = state.savedReceivers.filter((receiver) => receiver.id !== id);
                state.statuses.deleting = "fulfilled";
                state.errors.deleting = null;
            })
            .addCase(deleteSavedReceiver.rejected, (state, action) => {
                state.statuses.deleting = "rejected";
                if (action.payload?.err === "unauthorized") {
                    state.unauthorized = true;
                    return
                }
                if (action.payload?.err) {
                    state.errors.deleting = action.payload.err;
                }
                state.errors.deleting = "unable_to_update_saved_receiver";
            })
    },
});

export const {
    openUpdatingSavedReceiverPopup,
    closeUpdatingSavedReceiverPopup,
    setSavedReceiversUpdatingError,
    setSavedReceiversUpdatingStatus,
    openDeleteSavedReceiverPopup,
    closeDeleteSavedReceiverPopup,
    setUnauthorized
} = savedReceiversSlice.actions;

export const selectSavedReceivers = (state) =>
    state.savedReceivers.savedReceivers;

export const selectSavedReceiversStatuses = (state) =>
    state.savedReceivers.statuses;
export const selectSavedReceiversErrors = (state) =>
    state.savedReceivers.errors;

export const selectUpdatingSavedReceiverPopup = (state) =>
    state.savedReceivers.updatingPopup;

export const selectDeleteSavedReceiverPopup = (state) =>
    state.savedReceivers.deletePopup;

export default savedReceiversSlice.reducer;
